/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { map, sum } from 'lodash';
import { StyleSheet, css } from 'aphrodite';
import { BrandColors, Header } from '@blend/enterprise-components';
import { getPercentagesFromWidths } from '../../utils/ledgerUtils';

const styles = StyleSheet.create({
  ledgerHeaderItem: {
    padding: '12px 4px 12px 0',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  ledgerHeaderRow: {
    alignItems: 'center',
    borderBottom: `1px solid ${BrandColors.CLOUD}`,
    display: 'flex',
    paddingRight: 25,
    width: '100%',
  },
});

export namespace LedgerHeader {
  export interface Props {
    columnWidths: number[];
    headerComponents?: React.ReactNode[];
    headerTexts?: string[];
    rowActionHeaders?: {
      actionHeaders?: React.ReactNode[];
      columnWidths: number[];
    };
  }
}

export const LedgerHeader: React.FC<LedgerHeader.Props> = ({
  headerComponents,
  headerTexts,
  columnWidths,
  rowActionHeaders = { actionHeaders: [], columnWidths: [] },
}) => {
  const renderHeaderItem = (headerComponentOrText: React.ReactNode) => {
    if (headerComponents) {
      return headerComponentOrText;
    }

    return (
      <Header ellipsis size={6}>
        {headerComponents}
      </Header>
    );
  };

  const renderActionHeaderItems = (totalWidth: number) => {
    const actionWidthPercentages = getPercentagesFromWidths(rowActionHeaders.columnWidths, totalWidth);

    if (!rowActionHeaders.actionHeaders) {
      return map(rowActionHeaders.columnWidths, (_colWidth, idx) => (
        <div
          className={css(styles.ledgerHeaderItem)}
          key={`LedgerHeaderActionItem-${idx}`}
          style={{ width: actionWidthPercentages[idx] }}
        />
      ));
    }

    return map(rowActionHeaders.actionHeaders, (item: React.ReactNode, idx) => (
      <div
        className={css(styles.ledgerHeaderItem)}
        key={`LedgerHeaderActionItem-${idx}`}
        style={{ width: actionWidthPercentages[idx] }}
      >
        {item}
      </div>
    ));
  };

  const totalWidth = sum(columnWidths) + sum(rowActionHeaders.columnWidths);
  const colWidthsPercentage = getPercentagesFromWidths(columnWidths, totalWidth);
  const headerItems = headerTexts || headerComponents;
  return (
    <div className={css(styles.ledgerHeaderRow)}>
      {map(headerItems, (item, idx) => (
        <div
          className={css(styles.ledgerHeaderItem)}
          key={`LedgerHeaderItem-${idx}`}
          style={{ width: colWidthsPercentage[idx] }}
        >
          {renderHeaderItem(item)}
        </div>
      ))}
      {renderActionHeaderItems(totalWidth)}
    </div>
  );
};
